// src/features/ucits-etf/components/CapitalFlowsDashboard.tsx
//
// The "Smart Money" capital-flows board. Consumes the /ucits-etfs/top-flows
// envelope (top_inflows / top_outflows) and renders three things:
//
//   1. Two Recharts horizontal bar panels — inflows (emerald) and outflows
//      (rose) — for instant visual impact.
//   2. A unified @tanstack/react-table "Flows Explorer" with click-to-sort
//      headers and two custom numeric filters (Min AUM, Min |Flow|) applied
//      in-memory over the merged in/out rows.
//
// Net flow is the inferred 1-day capital movement (positive = money IN,
// negative = money OUT) computed by the worker from the day-over-day AUM delta
// stripped of NAV performance. `null` flows are excluded by the backend, so
// every row here carries a real number.
//
// Design: dark-mode aware (slate/emerald/rose), reuses the shared
// `surface-card*` utilities, and every ticker deep-links to its ETF Info page
// exactly like <BreakoutBoard /> and the similarity modal.

import { useMemo, useState } from 'react';

import { ArrowDownRight, ArrowUpRight, Waves } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
    Bar,
    BarChart,
    Cell,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';

import { useTopFlows } from '../api/etfQueries';
import { etfInfoPath } from '../routes';
import type { FundFlowRecord } from '../types';

const INFLOW_COLOR = '#10b981'; // emerald-500
const OUTFLOW_COLOR = '#f43f5e'; // rose-500

/**
 * Compact, currency-aware institutional formatter driven by the fund's
 * reporting currency: €11.28B / £266.60M / CHF 12.30K / $950. Preserves the
 * sign so a negative flow renders as "-€1.12B".
 *
 * Falls back to USD when the backend did not supply a currency, and degrades to
 * a plain "<CODE> 1.23B" string if the runtime rejects an unknown ISO code
 * (so a bad FMP currency can never throw and blank the dashboard).
 */
export const formatCurrency = (
    value: number | null | undefined,
    currencyCode?: string | null,
): string => {
    if (value === null || value === undefined) return '—';
    const code = (currencyCode || 'USD').toUpperCase();
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: code,
            notation: 'compact',
            maximumFractionDigits: 2,
        }).format(value);
    } catch {
        const sign = value < 0 ? '-' : '';
        const abs = Math.abs(value);
        if (abs >= 1e9) return `${sign}${code} ${(abs / 1e9).toFixed(2)}B`;
        if (abs >= 1e6) return `${sign}${code} ${(abs / 1e6).toFixed(2)}M`;
        if (abs >= 1e3) return `${sign}${code} ${(abs / 1e3).toFixed(2)}K`;
        return `${sign}${code} ${abs.toFixed(0)}`;
    }
};

/**
 * Symbol-free compact number formatter for the chart X-axis. A single flow
 * panel can mix funds denominated in different currencies, so the shared axis
 * stays currency-neutral (1.95B / 266.60M) while per-bar tooltips and the table
 * render the correct symbol.
 */
const formatCompact = (value: number): string =>
    new Intl.NumberFormat('en-US', {
        notation: 'compact',
        maximumFractionDigits: 2,
    }).format(value);

/**
 * Clickable Recharts Y-axis category tick: renders the fund symbol as an
 * underlined, pointer-cursored label that deep-links to the ETF Info page.
 * (Recharts renders inside an SVG, so we can't use <Link>; we navigate with a
 * plain anchor-style click handler instead.)
 */
const ClickableYAxisTick = (props: {
    x?: number;
    y?: number;
    payload?: { value: string };
}) => {
    const { x, y, payload } = props;
    const symbol = payload?.value ?? '';
    return (
        <text
            x={x}
            y={y}
            dy={4}
            textAnchor="end"
            style={{
                fill: '#64748b',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                textDecoration: 'underline',
            }}
            onClick={() => {
                if (symbol)
                    window.open(
                        etfInfoPath(symbol),
                        '_blank',
                        'noopener,noreferrer',
                    );
            }}
        >
            {symbol}
        </text>
    );
};

interface FlowChartProps {
    title: string;
    icon: React.ReactNode;
    rows: FundFlowRecord[];
    color: string;
    accent: string;
}

const FlowChart = ({ title, icon, rows, color, accent }: FlowChartProps) => (
    <div className="surface-card flex flex-col !p-0 overflow-hidden">
        <div
            className={`px-6 py-5 border-b border-slate-100 dark:border-slate-700/60 flex items-center gap-3 ${accent}`}
        >
            {icon}
            <h3 className="font-extrabold text-sm tracking-widest uppercase">
                {title}
            </h3>
        </div>
        <div className="p-4 h-72">
            {rows.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm font-medium text-slate-400 dark:text-slate-500">
                    No flow data
                </div>
            ) : (
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={rows}
                        layout="vertical"
                        margin={{ top: 4, right: 24, left: 8, bottom: 4 }}
                    >
                        <XAxis
                            type="number"
                            tickFormatter={(val: number) => formatCompact(val)}
                            tick={{ fontSize: 11, fill: '#94a3b8' }}
                            axisLine={false}
                            tickLine={false}
                        />
                        <YAxis
                            dataKey="symbol"
                            type="category"
                            width={84}
                            axisLine={false}
                            tickLine={false}
                            tick={<ClickableYAxisTick />}
                        />
                        <Tooltip
                            cursor={{ fill: 'rgba(148,163,184,0.12)' }}
                            formatter={(val: number, _name, item) => [
                                formatCurrency(
                                    val,
                                    (item?.payload as FundFlowRecord | undefined)
                                        ?.currency,
                                ),
                                'Net Flow',
                            ]}
                            contentStyle={{
                                borderRadius: '12px',
                                border: 'none',
                                boxShadow: '0 4px 12px -1px rgb(0 0 0 / 0.15)',
                            }}
                        />
                        <Bar dataKey="net_flow_1d" radius={[0, 4, 4, 0]} barSize={18}>
                            {rows.map((entry) => (
                                <Cell key={entry.symbol} fill={color} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            )}
        </div>
    </div>
);

export const CapitalFlowsDashboard = () => {
    const { data, isLoading, isError } = useTopFlows(20);

    // Custom numeric filters (applied in-memory over the merged rows).
    const [minAumFilter, setMinAumFilter] = useState<string>('');
    const [minFlowFilter, setMinFlowFilter] = useState<string>('');

    const tableData = useMemo<FundFlowRecord[]>(() => {
        if (!data) return [];
        let merged = [...data.top_inflows, ...data.top_outflows];

        if (minAumFilter) {
            const min = Number(minAumFilter);
            merged = merged.filter(
                (row) => row.aum !== null && row.aum >= min,
            );
        }
        if (minFlowFilter) {
            // Filter by MAGNITUDE so a -$5M outflow passes a $4M threshold.
            const min = Number(minFlowFilter);
            merged = merged.filter(
                (row) =>
                    row.net_flow_1d !== null &&
                    Math.abs(row.net_flow_1d) >= min,
            );
        }
        return merged;
    }, [data, minAumFilter, minFlowFilter]);

    const columns = useMemo(() => {
        const col = createColumnHelper<FundFlowRecord>();
        return [
            col.accessor('symbol', {
                header: 'Ticker',
                cell: (info) => (
                    <Link
                        to={etfInfoPath(info.getValue())}
                        className="font-bold text-slate-900 dark:text-slate-100 hover:underline decoration-dotted underline-offset-4"
                    >
                        {info.getValue()}
                    </Link>
                ),
            }),
            col.accessor('name', {
                header: 'Fund Name',
                cell: (info) => (
                    <span
                        className="block max-w-[260px] truncate text-slate-500 dark:text-slate-400"
                        title={info.getValue()}
                    >
                        {info.getValue()}
                    </span>
                ),
            }),
            col.accessor('aum', {
                header: 'Total AUM',
                cell: (info) => (
                    <span className="font-mono tabular-nums text-slate-700 dark:text-slate-300">
                        {formatCurrency(
                            info.getValue(),
                            info.row.original.currency,
                        )}
                    </span>
                ),
            }),
            col.accessor('net_flow_1d', {
                header: '1D Net Flow',
                cell: (info) => {
                    const val = info.getValue();
                    if (val === null) return <span className="text-slate-400">—</span>;
                    const positive = val > 0;
                    return (
                        <span
                            className={`font-mono tabular-nums font-bold ${
                                positive
                                    ? 'text-emerald-600 dark:text-emerald-400'
                                    : 'text-rose-600 dark:text-rose-400'
                            }`}
                        >
                            {positive ? '+' : ''}
                            {formatCurrency(val, info.row.original.currency)}
                        </span>
                    );
                },
            }),
        ];
    }, []);

    const table = useReactTable({
        data: tableData,
        columns,
        initialState: { sorting: [{ id: 'net_flow_1d', desc: true }] },
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    if (isLoading) return <div className="surface-card-skeleton h-[32rem]" />;

    if (isError) {
        return (
            <div className="surface-card-error">
                Unable to load capital flows right now. Please try again
                shortly.
            </div>
        );
    }

    const noFlows =
        !data ||
        (data.top_inflows.length === 0 && data.top_outflows.length === 0);

    if (noFlows) {
        return (
            <div className="surface-card text-center py-12">
                <p className="font-semibold text-slate-700 dark:text-slate-200">
                    Awaiting Flow Calculation
                </p>
                <p className="muted text-sm mt-1">
                    Capital flows need two consecutive days of pricing data to
                    diff against. Values appear once the nightly worker has run
                    on two separate days.
                </p>
            </div>
        );
    }

    // Charts show the top 10 per side; outflows sorted most-negative first.
    const chartInflows = data.top_inflows.slice(0, 10);
    const chartOutflows = [...data.top_outflows]
        .sort((a, b) => (a.net_flow_1d ?? 0) - (b.net_flow_1d ?? 0))
        .slice(0, 10);

    return (
        <div className="space-y-6">
            {/* Recharts panels. */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <FlowChart
                    title="Top Inflows · Capital Gained"
                    icon={<ArrowUpRight size={20} />}
                    rows={chartInflows}
                    color={INFLOW_COLOR}
                    accent="bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                />
                <FlowChart
                    title="Top Outflows · Capital Lost"
                    icon={<ArrowDownRight size={20} />}
                    rows={chartOutflows}
                    color={OUTFLOW_COLOR}
                    accent="bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400"
                />
            </div>

            {/* Advanced TanStack explorer. */}
            <div className="surface-card !p-0 overflow-hidden">
                <div className="p-5 border-b border-slate-100 dark:border-slate-700/60 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="text-indigo-600 dark:text-indigo-400">
                            <Waves size={20} />
                        </div>
                        <h3 className="font-extrabold text-sm tracking-widest uppercase text-slate-700 dark:text-slate-200">
                            Flows Explorer
                        </h3>
                    </div>
                    <div className="flex flex-wrap gap-4">
                        <div className="flex flex-col">
                            <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 mb-1">
                                Min AUM ($)
                            </label>
                            <input
                                type="number"
                                inputMode="numeric"
                                value={minAumFilter}
                                onChange={(e) => setMinAumFilter(e.target.value)}
                                placeholder="e.g. 500000000"
                                className="w-44 rounded-lg px-3 py-1.5 text-sm outline-none transition-colors
                                           bg-white dark:bg-slate-900/70
                                           border border-slate-300 dark:border-slate-600
                                           text-slate-900 dark:text-slate-100
                                           focus:ring-2 focus:ring-indigo-500"
                            />
                        </div>
                        <div className="flex flex-col">
                            <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 mb-1">
                                Min |Flow| ($)
                            </label>
                            <input
                                type="number"
                                inputMode="numeric"
                                value={minFlowFilter}
                                onChange={(e) => setMinFlowFilter(e.target.value)}
                                placeholder="e.g. 10000000"
                                className="w-44 rounded-lg px-3 py-1.5 text-sm outline-none transition-colors
                                           bg-white dark:bg-slate-900/70
                                           border border-slate-300 dark:border-slate-600
                                           text-slate-900 dark:text-slate-100
                                           focus:ring-2 focus:ring-indigo-500"
                            />
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/40 border-b border-slate-100 dark:border-slate-700/60">
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <th
                                            key={header.id}
                                            onClick={header.column.getToggleSortingHandler()}
                                            className="px-6 py-3 font-semibold cursor-pointer select-none transition-colors hover:bg-slate-100 dark:hover:bg-slate-700/40"
                                        >
                                            <div className="flex items-center gap-1.5">
                                                {flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext(),
                                                )}
                                                <span className="text-slate-400">
                                                    {{
                                                        asc: '↑',
                                                        desc: '↓',
                                                    }[
                                                        header.column.getIsSorted() as string
                                                    ] ?? ''}
                                                </span>
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {table.getRowModel().rows.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={columns.length}
                                        className="px-6 py-12 text-center text-sm font-medium text-slate-400 dark:text-slate-500"
                                    >
                                        No funds match the current filters.
                                    </td>
                                </tr>
                            ) : (
                                table.getRowModel().rows.map((row) => (
                                    <tr
                                        key={row.id}
                                        className="transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/40"
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <td key={cell.id} className="px-6 py-3.5">
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext(),
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default CapitalFlowsDashboard;
